import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FiPlay, FiPlus, FiCheck, FiX, FiThumbsUp, FiThumbsDown, FiShare2, FiStar, FiCalendar, FiClock, FiCopy
} from "react-icons/fi";
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  TelegramShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  TelegramIcon,
} from "react-share";
import { toast } from "react-toastify";
import { contentAPI, interactionAPI } from "../api/endpoints";
import { useAuth } from "../context/AuthContext";
import ContentRow from "../components/ContentRow";
import ImageWithPlaceholder from "../components/ImageWithPlaceholder";
import DragScrollRow from "../components/DragScrollRow";
import LoginPromptModal from "../components/LoginPromptModal";
import DetailsSkeleton from "../components/DetailsSkeleton";
import { Helmet } from "react-helmet-async";
import SEO from "../components/SEO";

export default function Details() {
  const { type, id } = useParams(); // type: "movie" | "series"
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();

  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [inWatchlist, setInWatchlist] = useState(false);
  const [userLike, setUserLike] = useState(null); // null | "like" | "dislike"
  const [likes, setLikes] = useState(0);
  const [dislikes, setDislikes] = useState(0);
  const [recommendations, setRecommendations] = useState([]);

  // TV show specific
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [episodesLoading, setEpisodesLoading] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);

  useEffect(() => {
    setLoading(true);
    setInWatchlist(false);

    contentAPI
      .getDetail(type, id)
      .then((res) => {
        const d = res.data;
        setContent(d);
        setLikes(d.like_count || 0);
        setDislikes(d.dislike_count || 0);
        // Use backend is_watchlist flag
        if (d.is_watchlist) setInWatchlist(true);

        // Map is_like/is_dislike to userLike state
        if (d.is_like) setUserLike("like");
        else if (d.is_dislike) setUserLike("dislike");
        else setUserLike(null);

        // Load seasons for series
        if (type === "series" || type === "tvshow") {
          contentAPI.getSeasons(id).then((seasonRes) => {
            const seasonList = seasonRes.data || [];
            setSeasons(seasonList);
            if (seasonList.length > 0) {
              setSelectedSeason(seasonList[0]);
              loadEpisodes(seasonList[0].id);
            }
          }).catch(() => { });
        }
      })
      .catch(() => toast.error("Failed to load content"))
      .finally(() => setLoading(false));

    // Load recommendations
    contentAPI.getRecommendations(id).then((res) => {
      setRecommendations(res.data || []);
    }).catch(() => { });
  }, [type, id]);

  const loadEpisodes = async (seasonId) => {
    setEpisodesLoading(true);
    try {
      const res = await contentAPI.getEpisodes(seasonId);
      setEpisodes(res.data || []);
    } catch {
      setEpisodes([]);
    }
    setEpisodesLoading(false);
  };

  const handleSeasonChange = (season) => {
    setSelectedSeason(season);
    loadEpisodes(season.id);
  };

  const handleLike = async () => {
    if (!isLoggedIn) return toast.info("Sign in to like content");
    try {
      await interactionAPI.likeDislike(id, "movies", "like");
      if (userLike === "like") {
        setUserLike(null);
        setLikes((p) => p - 1);
      } else {
        if (userLike === "dislike") setDislikes((p) => p - 1);
        setUserLike("like");
        setLikes((p) => p + 1);
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDislike = async () => {
    if (!isLoggedIn) return toast.info("Sign in to dislike content");
    try {
      await interactionAPI.likeDislike(id, "movies", "dislike");
      if (userLike === "dislike") {
        setUserLike(null);
        setDislikes((p) => p - 1);
      } else {
        if (userLike === "like") setLikes((p) => p - 1);
        setUserLike("dislike");
        setDislikes((p) => p + 1);
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleWatchlist = async () => {
    if (!isLoggedIn) return toast.info("Sign in to add to watchlist");
    try {
      const action = inWatchlist ? "delete" : "add";
      await interactionAPI.watchlistEvent(id, type, action);
      setInWatchlist(!inWatchlist);
      toast.success(inWatchlist ? "Removed from watchlist" : "Added to watchlist");
    } catch {
      toast.error("Failed to update watchlist");
    }
  };

  const handleCopyLink = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: content?.title, url }).catch(() => { });
    } else {
      navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard!");
    }
  };

  const toggleShareOptions = () => {
    setShowShareOptions(!showShareOptions);
  };

  // Close share options when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showShareOptions && !event.target.closest(".share-container")) {
        setShowShareOptions(false);
      }
    };
    if (showShareOptions) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showShareOptions]);

  const handlePlay = (episodeId) => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }
    if (episodeId) {
      navigate(`/player/${type}/${id}?episode=${episodeId}`);
    } else {
      navigate(`/player/${type}/${id}`);
    }
  };

  if (loading) {
    return <DetailsSkeleton />;
  }

  if (!content) return null;

  // Field mappings
  const posterImage = content.poster_image || content.poster_url || content.image;
  const coverImage = content.cover_image || content.image || content.poster_image;
  const title = content.title || content.name;
  const genre = content.genres || content.genre;
  const description = content.overview || `Watch ${title} on The Slay Network.`;
  const shareImage = posterImage || coverImage || "";
  const canonicalUrl = `https://theslaynetwork.tv/details/${type}/${id}`;
  const shareUrl = `https://theslaynetwork.tv/share.php?type=${type}&id=${id}`;

  return (
    <div className="min-h-screen bg-dark-900">
      <SEO
        title={title}
        description={description}
        image={shareImage}
        url={canonicalUrl}
        type="video.movie"
      />
      {/* ── HERO SECTION with Cover Image ── */}
      <div className="relative h-[50vh] md:h-[60vh]">
        <ImageWithPlaceholder
          src={coverImage}
          alt={title}
          className="w-full h-full object-cover"
          iconSize={48}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-900/60 to-dark-900/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-dark-900/80 to-transparent" />
      </div>

      {/* ── CONTENT INFO ── */}
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 -mt-48 relative z-10">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Poster */}
          <div className="shrink-0">
            <ImageWithPlaceholder
              src={posterImage}
              alt={title}
              className="w-48 md:w-56 rounded-xl shadow-2xl border border-dark-400 aspect-[2/3]"
              iconSize={36}
            />
          </div>

          {/* Info */}
          <div className="flex-1 pt-4">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">{title}</h1>

            {/* Meta Row */}
            <div className="flex flex-wrap items-center gap-3 mb-4 text-sm">
              {content.rating && (
                <span className="flex items-center gap-1 text-accent font-semibold">
                  <FiStar size={14} /> {content.rating}
                </span>
              )}
              {content.release_date && (
                <span className="flex items-center gap-1 text-gray-400">
                  <FiCalendar size={13} /> {content.release_date}
                </span>
              )}
              {content.duration && (
                <span className="flex items-center gap-1 text-gray-400">
                  <FiClock size={13} /> {content.duration}
                </span>
              )}
              {genre && (
                <span className="px-2.5 py-0.5 rounded-full bg-white/10 text-gray-300 text-xs">
                  {genre}
                </span>
              )}
              {content.is_18_plus && (
                <span className="px-2.5 py-0.5 rounded-full bg-red-600/30 text-red-400 text-xs font-semibold">
                  18+
                </span>
              )}
              {content.is_subtitle && (
                <span className="px-2.5 py-0.5 rounded-full bg-white/10 text-gray-300 text-xs">
                  Subtitles
                </span>
              )}
            </div>

            {/* Overview */}
            <p className="text-gray-400 text-sm leading-relaxed mb-6 max-w-2xl">
              {content.overview}
            </p>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              {(type != "series") && (
                <button
                  onClick={() => handlePlay()}
                  className="btn-play-gradient btn-play-gradient-sm"
                >
                  <FiPlay size={16} /> PLAY
                </button>
              )}
              <button
                onClick={handleWatchlist}
                className={`group/wl flex items-center gap-2 px-5 py-3 rounded-full font-medium text-sm transition-all border ${inWatchlist
                  ? "bg-white/10 border-green-500/50 text-green-400 hover:border-red-500/50 hover:text-red-400"
                  : "bg-white/5 border-white/20 text-white hover:border-white/40"
                  }`}
              >
                {inWatchlist ? (
                  <>
                    <FiCheck size={16} className="block group-hover/wl:hidden" />
                    <FiX size={16} className="hidden group-hover/wl:block" />
                    <span className="block group-hover/wl:hidden">In Watchlist</span>
                    <span className="hidden group-hover/wl:block">Remove</span>
                  </>
                ) : (
                  <>
                    <FiPlus size={16} />
                    Watchlist
                  </>
                )}
              </button>

              {/* Like / Dislike */}
              <div className="flex items-center gap-1 bg-dark-700 rounded-full border border-dark-400">
                <button
                  onClick={handleLike}
                  className={`flex items-center gap-1.5 px-4 py-2.5 rounded-l-full text-sm transition-colors ${userLike === "like" ? "text-green-400" : "text-gray-400 hover:text-white"
                    }`}
                >
                  <FiThumbsUp size={15} /> {likes}
                </button>
                <div className="w-px h-6 bg-dark-400" />
                <button
                  onClick={handleDislike}
                  className={`flex items-center gap-1.5 px-4 py-2.5 rounded-r-full text-sm transition-colors ${userLike === "dislike" ? "text-green-400" : "text-gray-400 hover:text-white"
                    }`}
                >
                  <FiThumbsDown size={15} /> {dislikes}
                </button>
              </div>

              {/* Share & Copy */}
              <div className="flex items-center gap-3 relative share-container">
                <div className="relative">
                  <button
                    onClick={toggleShareOptions}
                    className={`flex items-center gap-2 px-4 py-3 border rounded-full text-sm transition-all ${showShareOptions
                        ? "bg-white/20 border-white/40 text-white"
                        : "bg-white/5 border-white/20 text-gray-300 hover:text-white hover:border-white/40"
                      }`}
                  >
                    <FiShare2 size={15} /> Share
                  </button>

                  {showShareOptions && (
                    <div className="absolute bottom-full left-0 mb-3 bg-dark-800 border border-dark-400 rounded-2xl p-3 shadow-2xl z-50 flex gap-3 animate-fade-in-up">
                      <FacebookShareButton url={shareUrl} quote={title}>
                        <FacebookIcon size={32} round />
                      </FacebookShareButton>
                      <TwitterShareButton url={shareUrl} title={title}>
                        <TwitterIcon size={32} round />
                      </TwitterShareButton>
                      <WhatsappShareButton url={shareUrl} title={title} separator=":: ">
                        <WhatsappIcon size={32} round />
                      </WhatsappShareButton>
                      <TelegramShareButton url={shareUrl} title={title}>
                        <TelegramIcon size={32} round />
                      </TelegramShareButton>

                      {/* Triangle Arrow */}
                      <div className="absolute top-full left-6 -mt-1 border-8 border-transparent border-t-dark-800" />
                    </div>
                  )}
                </div>

                <button
                  onClick={handleCopyLink}
                  className="flex items-center gap-2 px-4 py-3 bg-white/5 border border-white/20 rounded-full text-sm text-gray-300 hover:text-white hover:border-white/40 transition-all"
                >
                  <FiCopy size={15} /> Copy Link
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── SEASONS & EPISODES (Series only) ── */}
        {(type === "series" || type === "tvshow") && seasons.length > 0 && (
          <div className="mt-12">
            <h2 className="text-xl font-bold text-white mb-4">Seasons & Episodes</h2>

            {/* Season Tabs */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
              {seasons.map((season) => (
                <button
                  key={season.id}
                  onClick={() => handleSeasonChange(season)}
                  className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${selectedSeason?.id === season.id
                    ? "bg-primary text-white"
                    : "bg-dark-700 text-gray-400 border border-dark-400 hover:text-white"
                    }`}
                >
                  {season.name || `Season ${season.season_number}`}
                </button>
              ))}
            </div>

            {/* Episodes List */}
            {episodesLoading ? (
              <div className="flex justify-center py-10">
                <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="space-y-3">
                {episodes.map((ep, i) => (
                  <div
                    key={ep.id}
                    onClick={() => handlePlay(ep.id)}
                    className="flex items-center gap-4 p-4 bg-dark-700 border border-dark-400 rounded-xl cursor-pointer hover:bg-dark-600 transition-colors group"
                  >
                    {/* Episode Number */}
                    <span className="text-2xl font-bold text-gray-600 w-8 text-center shrink-0">
                      {ep.episode_number || i + 1}
                    </span>

                    {/* Thumbnail */}
                    <div className="relative w-36 h-20 rounded-lg overflow-hidden shrink-0 bg-dark-800">
                      <ImageWithPlaceholder src={ep.image} alt={ep.name} className="w-full h-full object-cover" iconSize={20} />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40">
                        <FiPlay size={24} className="text-white" />
                      </div>
                    </div>

                    {/* Episode Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-white truncate">
                        {ep.name || `Episode ${ep.episode_number || i + 1}`}
                      </h4>
                      {ep.overview && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{ep.overview}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── TRAILERS ── */}
        {content.trailers?.length > 0 && (
          <div className="mt-12">
            <h2 className="text-xl font-bold text-white mb-4">Trailers</h2>
            <DragScrollRow className="gap-4">
              {content.trailers.map((trailer, i) => {
                const ytKey = trailer.link || trailer.youtube_trailer_key || trailer.key || trailer.url;
                const thumb = trailer.image || `https://img.youtube.com/vi/${ytKey}/hqdefault.jpg`;
                return (
                  <div key={i} className="shrink-0 w-80">
                    <a
                      href={`https://www.youtube.com/watch?v=${ytKey}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block relative aspect-video rounded-xl overflow-hidden bg-dark-700 group"
                    >
                      <ImageWithPlaceholder src={thumb} alt={trailer.title || `Trailer ${i + 1}`} className="w-full h-full object-cover" iconSize={28} />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors">
                        <FiPlay size={40} className="text-white drop-shadow-lg" />
                      </div>
                    </a>
                    {trailer.title && (
                      <p className="text-sm text-gray-400 mt-2">{trailer.title}</p>
                    )}
                  </div>
                );
              })}
            </DragScrollRow>
          </div>
        )}

        {/* ── CAST ── */}
        {content.casts?.length > 0 && (
          <div className="mt-12">
            <h2 className="text-xl font-bold text-white mb-4">Cast</h2>
            <DragScrollRow className="gap-4">
              {content.casts.map((person, i) => (
                <div key={i} className="shrink-0 text-center w-28">
                  <div className="w-20 h-20 mx-auto rounded-full overflow-hidden bg-dark-700 mb-2">
                    <ImageWithPlaceholder src={person.photo || person.image} alt={person.name} className="w-full h-full object-cover" iconSize={20} />
                  </div>
                  <p className="text-xs font-medium text-white truncate">{person.name}</p>
                  {person.title && (
                    <p className="text-[0.65rem] text-gray-400 truncate">{person.title}</p>
                  )}
                  {person.character && (
                    <p className="text-[0.65rem] text-gray-500 truncate">{person.character}</p>
                  )}
                </div>
              ))}
            </DragScrollRow>
          </div>
        )}

        {/* ── DIRECTORS ── */}
        {content.directors?.length > 0 && (
          <div className="mt-10">
            <h2 className="text-xl font-bold text-white mb-4">Directors</h2>
            <DragScrollRow className="gap-4">
              {content.directors.map((person, i) => (
                <div key={i} className="shrink-0 text-center w-28">
                  <div className="w-20 h-20 mx-auto rounded-full overflow-hidden bg-dark-700 mb-2">
                    <ImageWithPlaceholder src={person.photo || person.image} alt={person.name} className="w-full h-full object-cover" iconSize={20} />
                  </div>
                  <p className="text-xs font-medium text-white truncate">{person.name}</p>
                  {person.role && (
                    <p className="text-[0.65rem] text-gray-500 truncate">{person.role}</p>
                  )}
                </div>
              ))}
            </DragScrollRow>
          </div>
        )}

        {/* ── CREW ── */}
        {content.crew?.length > 0 && (
          <div className="mt-10">
            <h2 className="text-xl font-bold text-white mb-4">Crew</h2>
            <DragScrollRow className="gap-4">
              {content.crew.map((person, i) => (
                <div key={i} className="shrink-0 text-center w-28">
                  <div className="w-20 h-20 mx-auto rounded-full overflow-hidden bg-dark-700 mb-2">
                    <ImageWithPlaceholder src={person.photo || person.image} alt={person.name} className="w-full h-full object-cover" iconSize={20} />
                  </div>
                  <p className="text-xs font-medium text-white truncate">{person.name}</p>
                  {person.title && (
                    <p className="text-[0.65rem] text-gray-400 truncate">{person.title}</p>
                  )}
                  {person.job && (
                    <p className="text-[0.65rem] text-gray-500 truncate">{person.job}</p>
                  )}
                </div>
              ))}
            </DragScrollRow>
          </div>
        )}

        {/* ── RECOMMENDATIONS ── */}
        {recommendations.length > 0 && (
          <div className="mt-12 pb-12">
            <ContentRow
              title="You Might Also Like"
              items={recommendations}
              type={type}
            />
          </div>
        )}
      </div>

      {/* Login prompt modal */}
      <LoginPromptModal show={showLoginModal} onClose={() => setShowLoginModal(false)} />
    </div>
  );
}
